import { type LinkComponent, createLink } from '@tanstack/react-router';
import { ButtonLevel, ButtonSize, RadialProgress, buttonClasses } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Link, type LinkProps, composeRenderProps } from 'react-aria-components';

interface RouterButtonLinkProps extends LinkProps {
  /**
   * Whether the button is loading
   *
   * @optional
   * @default false
   * @type boolean
   */
  isLoading?: boolean;
  /**
   * The variant of the button
   *
   * @optional
   * @default 'primary'
   * @type 'primary' | 'secondary' | 'ghost' | 'text-primary' | 'text-secondary' | 'gradient';
   */
  level?: ButtonLevel;
  /**
   * The size of the button
   *
   * @optional
   * @default 'lg'
   * @type 'xl' | 'lg' | 'md'
   */
  size?: ButtonSize;
  /**
   * The left icon of the button
   *
   * @optional
   * @type React.ReactNode
   */
  iconStart?: React.ReactNode;
  /**
   * The right icon of the button
   *
   * @optional
   * @type React.ReactNode
   */
  iconEnd?: React.ReactNode;
}

/**
 * Duplication of `Button` component from `@vizit/atoms` with the only difference that it uses
 * `Link` component from `react-aria-components` for the `a` tag instead of `button` tag.
 */
const ButtonLink = React.forwardRef<HTMLAnchorElement, RouterButtonLinkProps>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    children,
    className: classNameProp,
    isDisabled,
    isLoading = false,
    level = ButtonLevel.Primary,
    size = ButtonSize.Sm,
    iconStart,
    iconEnd,
    ...other
  } = props;

  const isIconOnly = !!(iconStart || iconEnd) && !children;

  return (
    <Link
      {...other}
      aria-label={isLoading ? 'Loading' : ariaLabel}
      ref={ref}
      className={composeRenderProps(classNameProp, (className, renderProps) =>
        cx(
          'button group',
          buttonClasses({ ...renderProps, level, size, isDisabled, isLoading, isIconOnly, rounded: false }),
          className,
        ),
      )}
      isDisabled={isDisabled || isLoading}
    >
      {(value) => (
        <>
          {isLoading && <RadialProgress isIndeterminate fillClassName="stroke-white" size={8} aria-label="Loading" />}
          {!isLoading && iconStart && iconStart}
          {!isLoading && (typeof children === 'function' ? children(value) : children)}
          {!isLoading && iconEnd && iconEnd}
        </>
      )}
    </Link>
  );
});

const TanstackRouterButtonLink = createLink(ButtonLink);

/**
 * The RouterButtonLink component is a styled button that uses Tanstack Router to navigate between pages.
 * It is a wrapper around the `Link` component from react-aria-components, so the HTML tag remains `a` and
 * it's still an accessible link. Uses the same stylings as `@vizit/atoms` `Button` component.
 *
 * @param {RouterButtonLinkProps} props - The props for the RouterButtonLink component.
 * @example
 * <RouterButtonLink level={RouterButtonLinkLevel.Primary} iconStart={<ArrowLeftIcon />} to="/">
 *   Home
 * </RouterButtonLink>
 */
const RouterButtonLink: LinkComponent<typeof ButtonLink> = (props) => {
  return <TanstackRouterButtonLink preload={'intent'} {...props} />;
};

export { RouterButtonLink, ButtonLevel as RouterButtonLinkLevel, ButtonSize as RouterButtonLinkSize };
export type { RouterButtonLinkProps };
